import { PiggyBank } from "lucide-react";
import { Card, CardContent } from "@/components/ui";
import { formatCurrency } from "@/lib/utils";
import type { DashboardReport } from "@/types";

interface SalesProfitSummaryProps {
  report: DashboardReport;
  startDate: string;
  endDate: string;
}

function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function SalesProfitSummary({
  report,
  startDate,
  endDate,
}: SalesProfitSummaryProps) {
  const sameDay = startDate === endDate;
  const period = sameDay
    ? `On ${formatDay(startDate)}`
    : `From ${formatDay(startDate)} to ${formatDay(endDate)}`;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <PiggyBank className="h-5 w-5 text-primary" />
        </div>
        <p className="text-sm leading-relaxed sm:text-base">
          {period}, gross sales were{" "}
          <span className="font-semibold text-foreground">
            {formatCurrency(report.totalRevenue)}
          </span>{" "}
          and, based on the cost entered on each item, your profit is{" "}
          <span className="font-semibold text-green-700">
            {formatCurrency(report.grossProfit)}
          </span>{" "}
          <span className="text-muted-foreground">
            ({formatCurrency(report.totalCost)} cost of goods ·{" "}
            {(report.profitMargin * 100).toFixed(1)}% margin)
          </span>
          .
        </p>
      </CardContent>
    </Card>
  );
}
